import {
  Component,
  ViewEncapsulation,
  OnChanges,
  OnInit,
  SimpleChanges,
  Input,
  EventEmitter,
  Output,
  HostListener,
} from '@angular/core';
import {
  onClickActionMonthlyView,
  AvailabilityRequest,
  Day,
  Job,
  OwnerBlock,
  Period,
  Property,
  Reservation,
} from 'src/models/schedule-weekly-view.model';
import { DateTime as LuxonDateTime } from 'luxon';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { getReservationDayStatus } from '../services/reservation-day-status';

/**
 * One reservation/owner-block span within a single week row of the enhanced
 * monthly view. Columns are 1-based grid lines on a 14-track grid (2 tracks
 * per day) so check-in/check-out land mid-day and back-to-back stays meet
 * without overlapping; `row` stacks overlapping spans (e.g. an inquiry over
 * a confirmed stay) into separate rows.
 */
interface MonthlySegment {
  key: string;
  reservation?: Reservation;
  block?: OwnerBlock;
  colStart: number;
  colEnd: number;
  row: number;
  continuesLeft: boolean;
  continuesRight: boolean;
}

interface MonthlyJobChip {
  key: string;
  job: Job | AvailabilityRequest;
  label: string;
}

interface MonthlyJobCell {
  dayIndex: number;
  day: Day;
  chips: MonthlyJobChip[];
}

interface MonthlyLane {
  propertyId: number;
  address: string;
  segments: MonthlySegment[];
  jobCells: MonthlyJobCell[];
}

interface MonthlyWeek {
  days: Day[];
  lanes: MonthlyLane[];
}

@Component({
    templateUrl: 'schedule-monthly-view.html',
    styleUrls: ['./schedule-monthly-view.scss'],
    selector: 'tidy-schedule-monthly-view',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ScheduleMonthlyViewComponent implements OnChanges, OnInit {

  @Input() selectedPeriodRange: Period;
  @Input() properties: Property[];
  today = LuxonDateTime.local().toISODate();
  month: number;
  year: number;
  days: Day[] = [];
  wasCardClicked = false;
   
  @Output() clickAction = new EventEmitter<onClickActionMonthlyView>();
  windowWidth: number;
  @HostListener('window:resize')
  onResize() {
    this.windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    this.totalJobsToShow = Object.keys(this.jobsQuantityBreakpoints).reduce(
      (prev, curr) => {
        if (windowHeight > parseInt(curr)) {
          return this.jobsQuantityBreakpoints[curr];
        }
        return prev;
      },
      0
    );
    this.calculateJobNameTruncateLength();
  }
  @Input() isLoading: boolean;
  totalJobsToShow: number;
  jobsQuantityBreakpoints = {
    '0': 1,
    '830': 2,
    '978': 3,
  };
  jobNamesLengthTruncateBreakpoints = {
    '0': 10,
    '1070': 15,
  };
  jobNameTruncateLength: number;
  language: string | null;
  enhancedWeeks: MonthlyWeek[] = [];

  constructor() {}

  /**
   * With 3 or fewer properties the monthly view upgrades to per-property swim
   * lanes (reservations drawn as full-range bars). Beyond that the classic
   * "1 in / 1 out" day-cell view remains.
   */
  get isEnhancedView(): boolean {
    return (this.properties?.length ?? 0) > 0 && this.properties.length <= 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    this.totalJobsToShow = Object.keys(this.jobsQuantityBreakpoints).reduce(
      (prev, curr) => {
        if (windowHeight > parseInt(curr)) {
          return this.jobsQuantityBreakpoints[curr];
        }
        return prev;
      },
      0
    );
    this.calculateJobNameTruncateLength();
    if (changes['selectedPeriodRange'] || changes['properties']) {
      const month = LuxonDateTime.fromISO(
        this.selectedPeriodRange.endDate
      ).month;
      const year = LuxonDateTime.fromISO(this.selectedPeriodRange.endDate).year;
      this.generateCalendar(month, year);
    }
  }

  ngOnInit(): void {
    this.language = localStorage.getItem('language');
    this.windowWidth = window.innerWidth;
    this.month = LuxonDateTime.local().month;
    this.year = LuxonDateTime.local().year;
    this.calculateJobNameTruncateLength();
  }

  /**
   * Generate the calendar for the given month and year
   * @param month The month to generate the calendar for
   * @param year The year to generate the calendar for
   */
  generateCalendar(month: number, year: number): void {
    this.days = [];
    const firstDay = LuxonDateTime.local(year, month, 1).weekday % 7;
    const lastDate = LuxonDateTime.local(year, month).endOf('month').day;
    let prevLastDate = LuxonDateTime.local(year, month - 1).endOf('month').day;
    let isPreviousYear = false;
    if (isNaN(prevLastDate)) {
      prevLastDate = LuxonDateTime.local(year - 1, 12).endOf('month').day;
      isPreviousYear = true;
    }
    const monthName = LuxonDateTime.local(year, month).monthLong;
    const translatedMonth = new TranslationPipe().transform(monthName);
    const shortMonth = translatedMonth.slice(0, 3);
    // INFO: Fill in the days from the previous month
    for (let i = firstDay; i > 0; i--) {
      this.days.push({
        date: prevLastDate - i + 1,
        otherMonth: true,
        previousMonth: true,
        isWeekend: false,
        isToday: false,
        nextMonth: false,
        isFirstWeek: true,
        fullDate: LuxonDateTime.local(
          isPreviousYear ? year - 1 : year,
          isPreviousYear ? 12 : month - 1,
          prevLastDate - i + 1
        ).toISODate(),
      });
    }

    // INFO: Fill in the days of the current month
    for (let i = 1; i <= lastDate; i++) {
      const isToday =
        LuxonDateTime.local().day === i &&
        LuxonDateTime.local().month === month &&
        LuxonDateTime.local().year === year;
      this.days.push({
        date: i,
        shortMonth,
        otherMonth: false,
        previousMonth: false,
        isWeekend: false,
        isFirstWeek: this.days.length < 7,
        isToday,
        nextMonth: false,
        fullDate: LuxonDateTime.local(year, month, i).toISODate(),
      });
    }
    // Fill in the days of the next month
    const totalDays = firstDay + lastDate;
    const nextDays = (7 - (totalDays % 7)) % 7;
    let nextMonthLong = LuxonDateTime.local(year, month + 1).monthLong;
    let isNextYear = false;
    if (nextMonthLong === null) {
      nextMonthLong = LuxonDateTime.local(year + 1, 1).monthLong;
      isNextYear = true;
    }
    const nextMonthTranslated = new TranslationPipe().transform(nextMonthLong);
    const nextMonthShort = nextMonthTranslated.slice(0, 3);
    for (let i = 1; i <= nextDays; i++) {
      this.days.push({
        date: i,
        otherMonth: true,
        previousMonth: false,
        nextMonth: true,
        isWeekend: false,
        isToday: false,
        isFirstWeek: false,
        shortMonth: nextMonthShort,
        fullDate: LuxonDateTime.local(
          isNextYear ? year + 1 : year,
          isNextYear ? 1 : month + 1,
          i
        ).toISODate(),
      });
    }

    // INFO: Mark weekends
    this.days.forEach((day) => {
      const date = LuxonDateTime.fromISO(day.fullDate);
      const dayOfWeek = date.weekday;
      if (dayOfWeek === 6 || dayOfWeek === 7) {
        day.isWeekend = true;
        day['isSaturday'] = dayOfWeek === 6;
      }
      // Open popups to the left for the rightmost columns so they don't get
      // clipped by the calendar grid's right edge.
      day['popupLeft'] = dayOfWeek === 5 || dayOfWeek === 6;
    });

    this.addJobsToDays();
    this.addReservationsToDays();
    this.addOwnerBlocksToDays();
    this.buildEnhancedWeeks();
  }

  /**
   * Chunk the calendar grid into week rows and, per week, build one swim lane
   * per property holding its reservation/block spans and job chips.
   */
  buildEnhancedWeeks(): void {
    this.enhancedWeeks = [];
    if (!this.isEnhancedView || this.days.length === 0) {
      return;
    }
    const weekCount = Math.floor(this.days.length / 7);
    for (let weekIndex = 0; weekIndex < weekCount; weekIndex++) {
      const weekDays = this.days.slice(weekIndex * 7, weekIndex * 7 + 7);
      const lanes = this.properties
        .map((property) => this.buildLane(property, weekDays, weekIndex))
        .filter((lane) => lane.segments.length > 0 || lane.jobCells.length > 0);
      this.enhancedWeeks.push({ days: weekDays, lanes });
    }
  }

  private buildLane(property: Property, weekDays: Day[], weekIndex: number): MonthlyLane {
    const weekStart = weekDays[0].fullDate;
    const weekEnd = weekDays[6].fullDate;
    const segments: MonthlySegment[] = [];
    (property.filteredReservations ?? []).forEach((reservation) => {
      const start = reservation?.period?.startDate || reservation?.startDate;
      const end = reservation?.period?.endDate || reservation?.endDate;
      const segment = this.buildSegment(start, end, weekDays, weekStart, weekEnd, false);
      if (!segment) { return; }
      segments.push({
        ...segment,
        key: `reservation-${reservation.id}-${weekIndex}`,
        reservation,
      });
    });
    (property.ownerBlocks ?? []).forEach((block) => {
      const start = block?.period?.startDate || block?.startDate;
      const end = block?.period?.endDate || block?.endDate;
      // Block dates are nights (end inclusive), not a checkout day — span the
      // whole last day rather than ending mid-day like a reservation.
      const segment = this.buildSegment(start, end, weekDays, weekStart, weekEnd, true);
      if (!segment) { return; }
      segments.push({
        ...segment,
        key: `block-${block.id}-${weekIndex}`,
        block,
      });
    });
    this.assignSegmentRows(segments);
    const jobCells: MonthlyJobCell[] = [];
    weekDays.forEach((day, dayIndex) => {
      const chips: MonthlyJobChip[] = [
        ...(property.filteredJobs ?? [])
          .filter((job) => job.startDate === day.fullDate)
          .map((job) => ({ key: `job-${job.id}`, job, label: this.getLaneJobLabel(job) })),
        ...(property.filteredAvailabilityRequests ?? [])
          .filter((request) => request.startDate === day.fullDate)
          .map((request) => ({ key: `request-${request.id}`, job: request, label: this.getLaneJobLabel(request) })),
      ];
      if (chips.length > 0) {
        jobCells.push({ dayIndex, day, chips });
      }
    });
    return {
      propertyId: property.id,
      address: property.name || property.address || '',
      segments,
      jobCells,
    };
  }

  private buildSegment(
    start: string,
    end: string,
    weekDays: Day[],
    weekStart: string,
    weekEnd: string,
    isFullDaySpan: boolean
  ): Pick<MonthlySegment, 'colStart' | 'colEnd' | 'row' | 'continuesLeft' | 'continuesRight'> | null {
    if (!start || !end || end < weekStart || start > weekEnd) {
      return null;
    }
    const continuesLeft = start < weekStart;
    const continuesRight = end > weekEnd;
    const startIndex = continuesLeft ? 0 : weekDays.findIndex((day) => day.fullDate === start);
    const endIndex = continuesRight ? 6 : weekDays.findIndex((day) => day.fullDate === end);
    if (startIndex === -1 || endIndex === -1) {
      return null;
    }
    // 2 grid tracks per day: reservations start/end mid-day (check-in noon to
    // check-out noon), owner blocks cover their days edge to edge.
    const colStart = continuesLeft ? 1 : startIndex * 2 + (isFullDaySpan ? 1 : 2);
    const colEnd = continuesRight ? 15 : endIndex * 2 + (isFullDaySpan ? 3 : 2);
    return {
      colStart,
      colEnd: Math.max(colEnd, colStart + 1),
      row: 1,
      continuesLeft,
      continuesRight,
    };
  }

  /**
   * Greedy interval partitioning: spans that overlap in time stack into
   * separate rows, so bars never overlap each other.
   */
  private assignSegmentRows(segments: MonthlySegment[]): void {
    const sorted = [...segments].sort((a, b) => a.colStart - b.colStart || a.colEnd - b.colEnd);
    const rowEnds: number[] = [];
    sorted.forEach((segment) => {
      let row = rowEnds.findIndex((end) => end <= segment.colStart);
      if (row === -1) {
        row = rowEnds.length;
        rowEnds.push(segment.colEnd);
      } else {
        rowEnds[row] = segment.colEnd;
      }
      segment.row = row + 1;
    });
  }

  // The chip sits inside its property's lane, so it leads with the service
  // instead of repeating the address (which lives in the hover details).
  private getLaneJobLabel(job: any): string {
    return job?.object?.service_name
      || job?.object?.service_type_details?.name
      || job?.serviceCategory
      || job?.title
      || '';
  }

  /**
   * Calculate the job name truncate length based on window width
   */
  calculateJobNameTruncateLength(): void {
    const breakpoints = Object.entries(this.jobNamesLengthTruncateBreakpoints)
      .sort(([a], [b]) => parseInt(b) - parseInt(a));

    for (const [width, truncateSize] of breakpoints) {
      if (this.windowWidth > parseInt(width)) {
        this.jobNameTruncateLength = truncateSize;
        return;
      }
    }

    this.jobNameTruncateLength = breakpoints[breakpoints.length - 1][1];
  }

  addJobsToDays(): void {
    try {
      this.days.forEach((day) => {
        const filteredJobs = this.properties
          .map((property) =>
            property.filteredJobs.filter(
              (job) => job.startDate === day.fullDate
            )
          )
          .flat();
        const filteredAvailabilityRequests = this.properties
          .map((property) =>
            property.filteredAvailabilityRequests.filter(
              (availability) => availability.startDate === day.fullDate
            )
          )
          .flat();
        day.filteredJobs = [...filteredJobs, ...filteredAvailabilityRequests];
      });
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }
  }

  addReservationsToDays(): void {
    try {
      this.days.forEach((day) => {
        const rowsAtThisDay = this.properties
          .map((property) =>
            property.filteredReservations.filter(
              (reservation) =>
                reservation?.startDate === day.fullDate ||
                reservation?.endDate === day.fullDate
            )
          )
          .flat();
        // Inquiries ride the same calendar layer as reservations but are not
        // booked stays, so they get their own badge: counting them as check-ins
        // /check-outs both overstates the day and sends the click to the
        // Reservations modal, which has nothing to show for an inquiry.
        const filteredReservations = rowsAtThisDay.filter(
          (reservation) => reservation?.state !== 'inquiry'
        );
        day.filteredReservations = filteredReservations;
        // Per-day check-in/check-out status is derived client-side (works for
        // both reservations and inquiries; the API no longer sends it).
        const startingReservationsAtThisDay = filteredReservations.filter(
          (reservation) => getReservationDayStatus(reservation, day.fullDate) === 'check_in'
        );
        const endingReservationsAtThisDay = filteredReservations.filter(
          (reservation) => getReservationDayStatus(reservation, day.fullDate) === 'check_out'
        );
        day.reservationInfo = {
          starting: startingReservationsAtThisDay.length,
          ending: endingReservationsAtThisDay.length,
        };
        // Calculate reservation text once and store it
        (day as any).reservationText = this.calculateReservationText(day.reservationInfo);
        // Same in/out gate as the reservation badge: an inquiry that merely
        // spans this day (its stay was clamped to the visible month) is not
        // an event here, so it stays off the cell.
        day.filteredInquiries = rowsAtThisDay.filter((reservation) => {
          if (reservation?.state !== 'inquiry') {
            return false;
          }
          const status = getReservationDayStatus(reservation, day.fullDate);
          return status === 'check_in' || status === 'check_out';
        });
        day.inquiryText = this.calculateInquiryText(day.filteredInquiries);
      });
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }
  }

  /**
   * Marks each day with the owner blocks covering it (a block covers every
   * night from its real start to its real end, inclusive). The strip label
   * collapses to a count when several properties are blocked the same day.
   */
  addOwnerBlocksToDays(): void {
    try {
      this.days.forEach((day) => {
        const coveringBlocks = (this.properties ?? [])
          .map((property) =>
            (property.ownerBlocks ?? [])
              .filter((block) =>
                (block.period?.startDate ?? block.startDate) <= day.fullDate &&
                day.fullDate <= (block.period?.endDate ?? block.endDate)
              )
              .map((block): OwnerBlock => ({
                ...block,
                propertyName: property?.name || property?.address || '',
              }))
          )
          .flat();
        day.ownerBlocks = coveringBlocks;
        (day as any).ownerBlockText = this.calculateOwnerBlockText(coveringBlocks);
      });
    } catch (_error) {
      // Intentionally empty - errors handled elsewhere
    }
  }

  calculateOwnerBlockText(blocks: OwnerBlock[]): string {
    if (!blocks || blocks.length === 0) {
      return '';
    }
    if (blocks.length === 1) {
      return new TranslationPipe().transform('Blocked');
    }
    return `${blocks.length} ${new TranslationPipe().transform('blocked')}`;
  }

  onClickJob(job: Job): void {
    this.wasCardClicked = true;
    this.clickAction.emit({
      type: 'job',
      job,
      properties: this.properties,
    });
  }

  // Lane chips live outside the day cells, so their clicks don't bubble into
  // onClickDay — emit directly instead of going through the wasCardClicked
  // debounce (which would swallow the next day click).
  onClickLaneJob(job: Job, event: MouseEvent): void {
    event.stopPropagation();
    this.clickAction.emit({
      type: 'job',
      job,
      properties: this.properties,
    });
  }

  async onClickDay(day: any): Promise<void> {
    const wasCardClicked = await new Promise((resolve) =>
      setTimeout(() => {
        if (this.wasCardClicked) {
          resolve(this.wasCardClicked);
          return;
        }
        resolve(null);
      }, 100)
    );
    if (wasCardClicked) {
      this.wasCardClicked = false;
      return;
    }
    this.clickAction.emit({
      type: 'day',
      day,
      properties: this.properties,
    });
  }

  async onClickReservation(day: any): Promise<void> {
    this.wasCardClicked = true;
    this.clickAction.emit({
      type: 'reservation',
      day,
      properties: this.properties,
    });
  }

  async onClickInquiry(day: any): Promise<void> {
    this.wasCardClicked = true;
    this.clickAction.emit({
      type: 'inquiry',
      day,
      properties: this.properties,
    });
  }

  calculateReservationText(reservationInfo: any): string {
    if (reservationInfo?.starting > 0 && reservationInfo?.ending > 0) {
      return `${reservationInfo.starting} ${new TranslationPipe().transform('in')} / ${reservationInfo.ending} ${new TranslationPipe().transform('out')}`;
    } else if (reservationInfo?.ending > 0) {
      return `${reservationInfo.ending} ${new TranslationPipe().transform('out')}`;
    } else if (reservationInfo?.starting > 0) {
      return `${reservationInfo.starting} ${new TranslationPipe().transform('in')}`;
    }
    return '';
  }

  calculateInquiryText(inquiries: Reservation[]): string {
    if (!inquiries?.length) {
      return '';
    }
    const noun = inquiries.length === 1 ? 'inquiry' : 'inquiries';
    return `${inquiries.length} ${new TranslationPipe().transform(noun)}`;
  }

}
